import React from 'react';
import { Ticket, TrendingUp, CreditCard, AlertCircle, Printer } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { toBanglaNumber } from '../../lib/utils';
const stats = [{
  label: 'আজকের বিক্রিত টিকিট',
  value: '৩৮৬',
  icon: Ticket,
  color: 'text-blue-600',
  bg: 'bg-blue-50'
}, {
  label: 'আজকের আয়',
  value: '৳৭,৭২০',
  icon: TrendingUp,
  color: 'text-emerald-700',
  bg: 'bg-emerald-50'
}, {
  label: 'মোট বিক্রিত টিকিট',
  value: '২৪,৮৫০',
  icon: Ticket,
  color: 'text-indigo-600',
  bg: 'bg-indigo-50'
}, {
  label: 'মোট আয়',
  value: '৳৪,৯৭,০০০',
  icon: TrendingUp,
  color: 'text-emerald-600',
  bg: 'bg-emerald-50'
}, {
  label: 'bKash পেমেন্ট',
  value: '৳২,৮৪,১০০',
  icon: CreditCard,
  color: 'text-[#e2136e]',
  bg: 'bg-[#e2136e]/10'
}, {
  label: 'Nagad পেমেন্ট',
  value: '৳২,১২,৯০০',
  icon: CreditCard,
  color: 'text-[#f7931e]',
  bg: 'bg-[#f7931e]/10'
}, {
  label: 'Failed Payments',
  value: '৩২',
  icon: AlertCircle,
  color: 'text-destructive',
  bg: 'bg-destructive/10'
}, {
  label: 'Printed Tickets',
  value: '২৪,৭৯৮',
  icon: Printer,
  color: 'text-slate-600',
  bg: 'bg-slate-100'
}];
const dailySales = [{
  day: '১৩ মে',
  tickets: 275
}, {
  day: '১৪ মে',
  tickets: 320
}, {
  day: '১৫ মে',
  tickets: 295
}, {
  day: '১৬ মে',
  tickets: 410
}, {
  day: '১৭ মে',
  tickets: 525
}, {
  day: '১৮ মে',
  tickets: 488
}, {
  day: '১৯ মে',
  tickets: 386
}];
const dailyEarnings = [{
  day: '১৩ মে',
  earnings: 5500
}, {
  day: '১৪ মে',
  earnings: 6400
}, {
  day: '১৫ মে',
  earnings: 5900
}, {
  day: '১৬ মে',
  earnings: 8200
}, {
  day: '১৭ মে',
  earnings: 10500
}, {
  day: '১৮ মে',
  earnings: 9760
}, {
  day: '১৯ মে',
  earnings: 7720
}];
const gatewayData = [{
  name: 'bKash',
  value: 57,
  amount: 284100,
  color: '#e2136e'
}, {
  name: 'Nagad',
  value: 43,
  amount: 212900,
  color: '#f7931e'
}];
const qtyDistribution = [{
  label: '১ টিকিট',
  value: 18
}, {
  label: '২ টিকিট',
  value: 26
}, {
  label: '৩ টিকিট',
  value: 21
}, {
  label: '৪ টিকিট',
  value: 15
}, {
  label: '৫ টিকিট',
  value: 12
}, {
  label: '১০ টিকিট',
  value: 8
}];
const GOVT_GREEN = '#006A4E';
const GOVT_GREEN_LIGHT = '#10B981';
const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({
  title,
  subtitle,
  children
}) => <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm" data-id="element-98">
    <div className="mb-4" data-id="element-99">
      <h3 className="text-base font-bold text-slate-900" data-id="element-100">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5" data-id="element-101">{subtitle}</p>}
    </div>
    <div className="h-64" data-id="element-102">
      <ResponsiveContainer width="100%" height="100%" data-id="element-103">
        {children as any}
      </ResponsiveContainer>
    </div>
  </div>;
export function AdminDashboard() {
  return <div className="space-y-6" data-id="element-104">
      <div data-id="element-105">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-106">ড্যাশবোর্ড</h1>
        <p className="text-slate-500" data-id="element-107">আজকের এবং সার্বিক পরিসংখ্যান</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-108">
        {stats.map((stat, i) => <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm" data-id="element-109">
            <div className="flex items-center gap-4" data-id="element-110">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg}`} data-id="element-111">
                <stat.icon className={`w-6 h-6 ${stat.color}`} data-id="element-112" />
              </div>
              <div className="min-w-0" data-id="element-113">
                <p className="text-sm text-slate-500 font-medium truncate" data-id="element-114">
                  {stat.label}
                </p>
                <h3 className="text-2xl font-bold text-slate-900 mt-1" data-id="element-115">
                  {stat.value}
                </h3>
              </div>
            </div>
          </div>)}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-116">
        <ChartCard title="দৈনিক টিকিট বিক্রয়" subtitle="গত ৭ দিনে বিক্রিত টিকিটের সংখ্যা" data-id="element-117">
          <LineChart data={dailySales} margin={{
          top: 10,
          right: 10,
          left: -10,
          bottom: 0
        }} data-id="element-118">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" data-id="element-119" />
            <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} data-id="element-120" />
            <YAxis stroke="#94a3b8" fontSize={12} data-id="element-121" />
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            fontSize: 13
          }} formatter={(v: number) => [`${toBanglaNumber(v)} টিকিট`, 'বিক্রয়']} data-id="element-122" />
            <Line type="monotone" dataKey="tickets" stroke={GOVT_GREEN} strokeWidth={3} dot={{
            fill: GOVT_GREEN,
            r: 5
          }} activeDot={{
            r: 7
          }} data-id="element-123" />
          </LineChart>
        </ChartCard>

        <ChartCard title="দৈনিক আয়" subtitle="গত ৭ দিনের আয় (৳)" data-id="element-124">
          <AreaChart data={dailyEarnings} margin={{
          top: 10,
          right: 10,
          left: 0,
          bottom: 0
        }} data-id="element-125">
            <defs data-id="element-126">
              <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-127">
                <stop offset="0%" stopColor={GOVT_GREEN} stopOpacity={0.4} data-id="element-128" />
                <stop offset="100%" stopColor={GOVT_GREEN} stopOpacity={0.02} data-id="element-129" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" data-id="element-130" />
            <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} data-id="element-131" />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `৳${toBanglaNumber(v >= 1000 ? `${v / 1000}k` : v)}`} data-id="element-132" />
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            fontSize: 13
          }} formatter={(v: number) => [`৳${toBanglaNumber(v.toLocaleString('en-IN'))}`, 'আয়']} data-id="element-133" />
            <Area type="monotone" dataKey="earnings" stroke={GOVT_GREEN} strokeWidth={3} fill="url(#earningsGradient)" data-id="element-134" />
          </AreaChart>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-135">
        <ChartCard title="পেমেন্ট গেটওয়ে তুলনা" subtitle="মোট আয়ের বিভাজন" data-id="element-136">
          <PieChart data-id="element-137">
            <Pie data={gatewayData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2} dataKey="value" label={({
            name,
            value
          }) => `${name} ${toBanglaNumber(value)}%`} labelLine={false} data-id="element-138">
              {gatewayData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-139" />)}
            </Pie>
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            fontSize: 13
          }} formatter={(v: number, name: string, props: any) => [`৳${toBanglaNumber(props.payload.amount.toLocaleString('en-IN'))} (${toBanglaNumber(v)}%)`, name]} data-id="element-140" />
            <Legend iconType="circle" formatter={v => <span className="text-sm text-slate-600" data-id="element-142">{v}</span>} data-id="element-141" />
          </PieChart>
        </ChartCard>

        <ChartCard title="টিকিট পরিমাণ বিতরণ" subtitle="ক্রেতারা সাধারণত কতগুলো টিকিট কেনেন" data-id="element-143">
          <BarChart data={qtyDistribution} layout="vertical" margin={{
          top: 5,
          right: 20,
          left: 10,
          bottom: 0
        }} data-id="element-144">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} data-id="element-145" />
            <XAxis type="number" stroke="#94a3b8" fontSize={12} tickFormatter={v => `${toBanglaNumber(v)}%`} data-id="element-146" />
            <YAxis dataKey="label" type="category" stroke="#94a3b8" fontSize={12} width={70} data-id="element-147" />
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            fontSize: 13
          }} formatter={(v: number) => [`${toBanglaNumber(v)}%`, 'অংশ']} data-id="element-148" />
            <Bar dataKey="value" fill={GOVT_GREEN_LIGHT} radius={[0, 8, 8, 0]} data-id="element-149" />
          </BarChart>
        </ChartCard>
      </div>
    </div>;
}